import { motion } from 'framer-motion';
import { Cpu, Brain } from 'lucide-react';

export default function OrdinaAIFlow() {
  return (
    <>
      {/* ===== MOBILE VERTICAL LAYOUT (shown on < 768px) ===== */}
      <div className="md:hidden relative w-full flex flex-col items-center justify-center gap-0 py-8 px-4">
        {/* Legacy Processes Card - Top */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <motion.div
            className="bg-gradient-to-br from-cyan-100/60 to-blue-100/60 backdrop-blur-md border border-cyan-300/60 rounded-full px-10 py-6 shadow-lg shadow-cyan-500/10 relative overflow-hidden flex flex-col items-center justify-center text-center"
            animate={{
              y: [0, -6, 0],
              boxShadow: [
                '0 10px 30px rgba(6, 182, 212, 0.12)',
                '0 20px 50px rgba(6, 182, 212, 0.25)',
                '0 10px 30px rgba(6, 182, 212, 0.12)',
              ],
              scale: [1, 1.02, 1],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-transparent"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ opacity: [0.08, 0.15, 0.08] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Cpu size={80} className="text-cyan-600" strokeWidth={1} />
            </motion.div>
            <p className="text-lg font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Legacy
            </p>
            <p className="text-lg font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Processes
            </p>
          </motion.div>
        </motion.div>

        {/* Vertical Connector - Top */}
        <div className="relative h-20 w-16 flex items-center justify-center">
          <svg className="w-full h-full overflow-visible" viewBox="0 0 60 80" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradientMV1" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#d0e8ef" />
                <stop offset="100%" stopColor="#06b6d4" />
              </linearGradient>
              <filter id="glowMV1">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <motion.path
              d="M 30 0 Q 45 20, 30 40 T 30 80"
              fill="none"
              stroke="url(#lineGradientMV1)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
            />
            {[0, 0.5, 1, 1.5, 2].map((delay, i) => (
              <motion.circle
                key={i}
                r={3 - i * 0.4}
                fill="#06b6d4"
                filter="url(#glowMV1)"
                animate={{
                  opacity: [0, 1, 1, 0],
                  offsetDistance: ['0%', '100%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: delay,
                  repeatDelay: 0.5,
                }}
                style={{
                  offsetPath: "path('M 30 0 Q 45 20, 30 40 T 30 80')",
                }}
              />
            ))}
          </svg>
        </div>

        {/* AI Agent Center - Full Animations for Mobile */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-40 h-40 flex items-center justify-center">
            {/* Pulsing concentric rings */}
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                className="absolute rounded-full border border-cyan-500"
                style={{
                  inset: i * 6,
                  opacity: 0.2 + i * 0.1,
                }}
                animate={{
                  scale: [1, 1.05 + i * 0.02, 1],
                  opacity: [0.15 + i * 0.05, 0.3 + i * 0.1, 0.15 + i * 0.05],
                }}
                transition={{
                  duration: 2 + i * 0.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.2,
                }}
              />
            ))}

            {/* Primary rotating ring with gradient */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
            >
              <svg className="w-full h-full" viewBox="0 0 160 160">
                <defs>
                  <linearGradient id="ringGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.9" />
                    <stop offset="25%" stopColor="#0891b2" stopOpacity="0.6" />
                    <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.3" />
                    <stop offset="75%" stopColor="#0891b2" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.9" />
                  </linearGradient>
                </defs>
                <circle
                  cx="80"
                  cy="80"
                  r="75"
                  fill="none"
                  stroke="url(#ringGradientMobile)"
                  strokeWidth="2"
                  strokeDasharray="40 20 10 20"
                />
              </svg>
            </motion.div>

            {/* Counter-rotating ring */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: -360 }}
              transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            >
              <svg className="w-full h-full" viewBox="0 0 160 160">
                <circle
                  cx="80"
                  cy="80"
                  r="62"
                  fill="none"
                  stroke="#06b6d4"
                  strokeWidth="1.5"
                  strokeDasharray="5 15 10 15"
                  opacity="0.5"
                />
              </svg>
            </motion.div>

            {/* Second counter-rotating ring */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
            >
              <svg className="w-full h-full" viewBox="0 0 160 160">
                <circle
                  cx="80"
                  cy="80"
                  r="50"
                  fill="none"
                  stroke="#0891b2"
                  strokeWidth="1"
                  strokeDasharray="2 12"
                  opacity="0.4"
                />
              </svg>
            </motion.div>

            {/* Central circle with glow */}
            <motion.div
              className="relative z-10 w-24 h-24 rounded-full bg-white/95 backdrop-blur-md border-2 border-cyan-300/60 flex items-center justify-center"
              animate={{
                boxShadow: [
                  '0 0 30px rgba(6, 182, 212, 0.2), inset 0 0 20px rgba(6, 182, 212, 0.05)',
                  '0 0 50px rgba(6, 182, 212, 0.4), inset 0 0 30px rgba(6, 182, 212, 0.1)',
                  '0 0 30px rgba(6, 182, 212, 0.2), inset 0 0 20px rgba(6, 182, 212, 0.05)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/10 to-transparent"
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
              />
              <motion.p
                className="text-cyan-600 text-lg font-semibold z-10"
                animate={{
                  textShadow: [
                    '0 0 10px rgba(6, 182, 212, 0.3)',
                    '0 0 20px rgba(6, 182, 212, 0.5)',
                    '0 0 10px rgba(6, 182, 212, 0.3)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                AI Agent
              </motion.p>
            </motion.div>

            {/* 8-point pulsing dots around center */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full bg-cyan-500"
                style={{
                  left: `calc(50% + ${Math.cos((angle * Math.PI) / 180) * 78}px)`,
                  top: `calc(50% + ${Math.sin((angle * Math.PI) / 180) * 78}px)`,
                }}
                animate={{
                  scale: [0.8, 1.5, 0.8],
                  opacity: [0.3, 0.9, 0.3],
                  boxShadow: [
                    '0 0 5px rgba(6, 182, 212, 0.5)',
                    '0 0 15px rgba(6, 182, 212, 0.8)',
                    '0 0 5px rgba(6, 182, 212, 0.5)',
                  ],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.15,
                  ease: 'easeInOut',
                }}
              />
            ))}

            {/* 3 orbiting particles at 120° intervals */}
            {[0, 120, 240].map((_, i) => (
              <motion.div
                key={`orbit-mobile-${i}`}
                className="absolute w-2.5 h-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600"
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'linear',
                  delay: i * 2,
                }}
                style={{
                  transformOrigin: '40px center',
                  left: '50%',
                  top: '50%',
                  marginLeft: -40,
                  marginTop: -5,
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Vertical Connector - Bottom */}
        <div className="relative h-20 w-16 flex items-center justify-center">
          <svg className="w-full h-full overflow-visible" viewBox="0 0 60 80" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradientMV2" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#d0e8ef" />
              </linearGradient>
              <filter id="glowMV2">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <motion.path
              d="M 30 0 Q 15 20, 30 40 T 30 80"
              fill="none"
              stroke="url(#lineGradientMV2)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.8 }}
            />
            {[0.8, 1.3, 1.8, 2.3, 2.8].map((delay, i) => (
              <motion.circle
                key={i}
                r={3 - i * 0.4}
                fill="#06b6d4"
                filter="url(#glowMV2)"
                animate={{
                  opacity: [0, 1, 1, 0],
                  offsetDistance: ['0%', '100%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: delay,
                  repeatDelay: 0.5,
                }}
                style={{
                  offsetPath: "path('M 30 0 Q 15 20, 30 40 T 30 80')",
                }}
              />
            ))}
          </svg>
        </div>

        {/* Smart Workflows Card - Bottom */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="relative"
        >
          <motion.div
            className="bg-gradient-to-br from-blue-100/60 to-cyan-100/60 backdrop-blur-md border border-cyan-300/60 rounded-full px-10 py-6 shadow-lg shadow-cyan-500/10 relative overflow-hidden flex flex-col items-center justify-center text-center"
            animate={{
              y: [0, -6, 0],
              boxShadow: [
                '0 10px 30px rgba(6, 182, 212, 0.12)',
                '0 20px 50px rgba(6, 182, 212, 0.25)',
                '0 10px 30px rgba(6, 182, 212, 0.12)',
              ],
              scale: [1, 1.02, 1],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-l from-cyan-500/5 to-transparent"
              animate={{ x: ['100%', '-100%'] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, delay: 1 }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ opacity: [0.08, 0.15, 0.08] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            >
              <Brain size={80} className="text-cyan-600" strokeWidth={1} />
            </motion.div>
            <p className="text-lg font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Smart
            </p>
            <p className="text-lg font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Workflows
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* ===== DESKTOP HORIZONTAL LAYOUT (shown on >= 768px) ===== */}
      <div className="hidden md:flex relative w-full items-center justify-center gap-0 min-w-0">
        {/* Legacy Processes Card - Far Left */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative flex-shrink-0"
          style={{ transform: 'translateX(-3rem)' }}
        >
          <motion.div
            className="bg-gradient-to-br from-cyan-100/60 to-blue-100/60 backdrop-blur-md border border-cyan-300/60 rounded-full px-12 py-8 shadow-lg shadow-cyan-500/10 relative overflow-hidden flex flex-col items-center justify-center text-center cursor-pointer"
            whileHover={{ scale: 1.1, y: -8, boxShadow: '0 35px 60px rgba(6, 182, 212, 0.3)' }}
            animate={{
              y: [0, -6, 0],
              boxShadow: [
                '0 10px 30px rgba(6, 182, 212, 0.12)',
                '0 20px 50px rgba(6, 182, 212, 0.25)',
                '0 10px 30px rgba(6, 182, 212, 0.12)',
              ],
              scale: [1, 1.02, 1],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-transparent"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ opacity: [0.08, 0.15, 0.08] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Cpu size={120} className="text-cyan-600" strokeWidth={1} />
            </motion.div>
            <p className="text-xl font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Legacy
            </p>
            <p className="text-xl font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Processes
            </p>
          </motion.div>
        </motion.div>

        {/* Center Section with Connectors and AI Agent */}
        <div className="relative flex-1 flex items-center justify-center gap-0">
          {/* Left Connector */}
          <div className="relative flex-1 h-16 flex items-center">
            <svg className="w-full h-full overflow-visible" viewBox="0 0 200 60" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#d0e8ef" />
                <stop offset="100%" stopColor="#06b6d4" />
              </linearGradient>
              <filter id="glow1">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <motion.path
              d="M 0 30 Q 50 10, 100 30 T 200 30"
              fill="none"
              stroke="url(#lineGradient1)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
            />
            {[0, 0.5, 1, 1.5, 2].map((delay, i) => (
              <motion.circle
                key={i}
                r={4 - i * 0.5}
                fill="#06b6d4"
                filter="url(#glow1)"
                animate={{
                  opacity: [0, 1, 1, 0],
                  offsetDistance: ['0%', '100%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: delay,
                  repeatDelay: 0.5,
                }}
                style={{
                  offsetPath: "path('M 0 30 Q 50 10, 100 30 T 200 30')",
                }}
              />
            ))}
            </svg>
          </div>

          {/* AI Agent Center */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-48 h-48 flex items-center justify-center">
            {/* Pulsing concentric rings */}
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                className="absolute rounded-full border border-cyan-500"
                style={{
                  inset: i * 8,
                  opacity: 0.2 + i * 0.1,
                }}
                animate={{
                  scale: [1, 1.05 + i * 0.02, 1],
                  opacity: [0.15 + i * 0.05, 0.3 + i * 0.1, 0.15 + i * 0.05],
                }}
                transition={{
                  duration: 2 + i * 0.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.2,
                }}
              />
            ))}

            {/* Primary rotating ring with gradient */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
            >
              <svg className="w-full h-full" viewBox="0 0 200 200">
                <defs>
                  <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.9" />
                    <stop offset="25%" stopColor="#0891b2" stopOpacity="0.6" />
                    <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.3" />
                    <stop offset="75%" stopColor="#0891b2" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.9" />
                  </linearGradient>
                </defs>
                <circle
                  cx="100"
                  cy="100"
                  r="95"
                  fill="none"
                  stroke="url(#ringGradient)"
                  strokeWidth="2"
                  strokeDasharray="40 20 10 20"
                />
              </svg>
            </motion.div>

            {/* Counter-rotating ring */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: -360 }}
              transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            >
              <svg className="w-full h-full" viewBox="0 0 200 200">
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#06b6d4"
                  strokeWidth="1.5"
                  strokeDasharray="5 15 10 15"
                  opacity="0.5"
                />
              </svg>
            </motion.div>

            {/* Second counter-rotating ring */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
            >
              <svg className="w-full h-full" viewBox="0 0 200 200">
                <circle
                  cx="100"
                  cy="100"
                  r="65"
                  fill="none"
                  stroke="#0891b2"
                  strokeWidth="1"
                  strokeDasharray="2 12"
                  opacity="0.4"
                />
              </svg>
            </motion.div>

            {/* Central circle with glow */}
            <motion.div
              className="relative z-10 w-32 h-32 rounded-full bg-white/95 backdrop-blur-md border-2 border-cyan-300/60 flex items-center justify-center"
              animate={{
                boxShadow: [
                  '0 0 30px rgba(6, 182, 212, 0.2), inset 0 0 20px rgba(6, 182, 212, 0.05)',
                  '0 0 50px rgba(6, 182, 212, 0.4), inset 0 0 30px rgba(6, 182, 212, 0.1)',
                  '0 0 30px rgba(6, 182, 212, 0.2), inset 0 0 20px rgba(6, 182, 212, 0.05)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/10 to-transparent"
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
              />
              <motion.p
                className="text-cyan-600 text-2xl font-semibold z-10"
                animate={{
                  textShadow: [
                    '0 0 10px rgba(6, 182, 212, 0.3)',
                    '0 0 20px rgba(6, 182, 212, 0.5)',
                    '0 0 10px rgba(6, 182, 212, 0.3)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                AI Agent
              </motion.p>
            </motion.div>

            {/* 8-point pulsing dots around center */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full bg-cyan-500"
                style={{
                  left: `calc(50% + ${Math.cos((angle * Math.PI) / 180) * 100}px)`,
                  top: `calc(50% + ${Math.sin((angle * Math.PI) / 180) * 100}px)`,
                }}
                animate={{
                  scale: [0.8, 1.5, 0.8],
                  opacity: [0.3, 0.9, 0.3],
                  boxShadow: [
                    '0 0 5px rgba(6, 182, 212, 0.5)',
                    '0 0 15px rgba(6, 182, 212, 0.8)',
                    '0 0 5px rgba(6, 182, 212, 0.5)',
                  ],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.15,
                  ease: 'easeInOut',
                }}
              />
            ))}

            {/* 3 orbiting particles at 120° intervals */}
            {[0, 120, 240].map((_, i) => (
              <motion.div
                key={`orbit-${i}`}
                className="absolute w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600"
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'linear',
                  delay: i * 2,
                }}
                style={{
                  transformOrigin: '50px center',
                  left: '50%',
                  top: '50%',
                  marginLeft: -50,
                  marginTop: -6,
                }}
              />
            ))}
          </div>
          </motion.div>

          {/* Right Connector */}
          <div className="relative flex-1 h-16 flex items-center">
            <svg className="w-full h-full overflow-visible" viewBox="0 0 200 60" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#d0e8ef" />
              </linearGradient>
              <filter id="glow2">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <motion.path
              d="M 0 30 Q 50 50, 100 30 T 200 30"
              fill="none"
              stroke="url(#lineGradient2)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.8 }}
            />
            {[0.8, 1.3, 1.8, 2.3, 2.8].map((delay, i) => (
              <motion.circle
                key={i}
                r={4 - i * 0.5}
                fill="#06b6d4"
                filter="url(#glow2)"
                animate={{
                  opacity: [0, 1, 1, 0],
                  offsetDistance: ['0%', '100%'],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: delay,
                  repeatDelay: 0.5,
                }}
                style={{
                  offsetPath: "path('M 0 30 Q 50 50, 100 30 T 200 30')",
                }}
              />
            ))}
            </svg>
          </div>
        </div>

        {/* Smart Workflows Card - Far Right */}
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="relative flex-shrink-0"
          style={{ transform: 'translateX(3rem)' }}
        >
          <motion.div
            className="bg-gradient-to-br from-blue-100/60 to-cyan-100/60 backdrop-blur-md border border-cyan-300/60 rounded-full px-12 py-8 shadow-lg shadow-cyan-500/10 relative overflow-hidden flex flex-col items-center justify-center text-center cursor-pointer"
            whileHover={{ scale: 1.1, y: -8, boxShadow: '0 35px 60px rgba(6, 182, 212, 0.3)' }}
            animate={{
              y: [0, -6, 0],
              boxShadow: [
                '0 10px 30px rgba(6, 182, 212, 0.12)',
                '0 20px 50px rgba(6, 182, 212, 0.25)',
                '0 10px 30px rgba(6, 182, 212, 0.12)',
              ],
              scale: [1, 1.02, 1],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-l from-cyan-500/5 to-transparent"
              animate={{ x: ['100%', '-100%'] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, delay: 1 }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ opacity: [0.08, 0.15, 0.08] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            >
              <Brain size={120} className="text-cyan-600" strokeWidth={1} />
            </motion.div>
            <p className="text-xl font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Smart
            </p>
            <p className="text-xl font-black text-slate-800 relative z-10 tracking-tight" style={{ fontFamily: 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, Arial Rounded, "Helvetica Rounded", sans-serif', letterSpacing: '-0.02em' }}>
              Workflows
            </p>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
}
